import React, { useCallback, useEffect, useState } from 'react';
import { Layers } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Field, Textarea } from '../ui/Field';
import { pillPrimary, pillSecondary } from '../ui/pills';
import { maFormuleApi, type FormuleVueClient, type MaFormule } from '../../lib/formulesApi';
import { messageErreur } from '../../lib/organisationApi';

/**
 * « Ma formule » (page Mes modules, compte principal du client) : la formule en cours avec son prix, ce qu'elle comprend, et la possibilité
 * de demander un changement. Le client ne change jamais seul sa formule : L'IAmani lit la demande et l'accepte ou la refuse.
 * Serveur : functions/api/plateforme-formules.js (maFormuleApp, monté sur /ma-formule).
 */
const euros = (n: number) => `${(Math.round((Number(n) || 0) * 100) / 100).toString().replace('.', ',')} €`;
const dateFr = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '');

const Contenu: React.FC<{ f: FormuleVueClient }> = ({ f }) => (
    <ul className="text-[12.5px] text-content-muted space-y-0.5 list-disc pl-4">
        {f.modules.map((m) => <li key={m}>{m}</li>)}
        {(f.salaries_inclus || 0) > 0 && <li>{f.salaries_inclus} salariés inclus dans le planning{(f.remise_equipe_pct || 0) > 0 ? `, puis -${String(f.remise_equipe_pct).replace('.', ',')} % sur les paliers` : ''}</li>}
        {f.sms_inclus > 0 && <li>{f.sms_inclus} SMS par mois</li>}
        {f.minutes_tel > 0 && <li>{f.minutes_tel} min de téléphone IA</li>}
    </ul>
);

export const MaFormuleCarte: React.FC = () => {
    const [d, setD] = useState<MaFormule | null>(null);
    const [ouvert, setOuvert] = useState(false);
    const [choix, setChoix] = useState('');
    const [message, setMessage] = useState('');
    const [erreur, setErreur] = useState('');
    const [envoi, setEnvoi] = useState(false);

    const charger = useCallback(() => { maFormuleApi.lire().then(setD).catch(() => setD(null)); }, []);
    useEffect(() => { charger(); }, [charger]);

    if (!d || (!d.formule && d.formules.length === 0)) return null;
    const autres = d.formules.filter((f) => f.id !== d.formule?.id);

    const envoyer = async () => {
        setEnvoi(true); setErreur('');
        try { await maFormuleApi.demander(choix, message); setOuvert(false); setMessage(''); charger(); }
        catch (e) { setErreur(messageErreur(e) || 'Envoi impossible'); } finally { setEnvoi(false); }
    };
    const annuler = async () => { try { await maFormuleApi.annuler(); } finally { charger(); } };
    const ouvrir = (id: string) => { setChoix(id); setErreur(''); setOuvert(true); };

    return (
        <section aria-label="Ma formule" className="rounded-xl border border-border bg-surface p-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                    <Layers size={18} className="mt-0.5 text-brand-600" />
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted">Ma formule</p>
                        {d.formule ? (
                            <p className="text-[15px] font-bold text-content">{d.formule.nom} · {euros(d.formule.prix_eur)} <span className="font-normal text-content-muted">/ mois, hors taxes{d.formule.debut ? ` · depuis le ${dateFr(d.formule.debut)}` : ''}</span></p>
                        ) : (
                            <p className="text-[15px] font-bold text-content">Modules à la carte <span className="font-normal text-content-muted">· chaque module est facturé selon son tarif</span></p>
                        )}
                    </div>
                </div>
                {!d.demande && autres.length > 0 && <button type="button" className={pillSecondary(36)} onClick={() => ouvrir(autres[0].id)}>Demander un changement</button>}
            </div>
            {d.formule && <Contenu f={d.formule} />}
            {d.demande && (
                <div role="status" className="rounded-lg bg-state-info-soft text-state-info px-3 py-2 text-[13px] flex flex-wrap items-center justify-between gap-2">
                    <span>Demande envoyée le {dateFr(d.demande.created_at)} : passer à « {d.demande.formule_nom} ». L’IAmani vous répond.</span>
                    <button type="button" className="underline font-semibold" onClick={annuler}>Annuler ma demande</button>
                </div>
            )}
            {!d.demande && d.derniere_refusee && (
                <p className="rounded-lg bg-state-warn/10 px-3 py-2 text-[13px] text-content">Votre dernière demande (« {d.derniere_refusee.formule_nom} ») n’a pas été acceptée{d.derniere_refusee.reponse ? ` : ${d.derniere_refusee.reponse}` : '.'}</p>
            )}
            <Modal open={ouvert} onClose={() => setOuvert(false)} size="md" title="Demander un changement de formule"
                footer={<><button type="button" className={pillSecondary(36)} onClick={() => setOuvert(false)}>Annuler</button><button type="button" className={pillPrimary(36)} disabled={envoi || !choix} onClick={envoyer}>Envoyer la demande</button></>}>
                <div className="space-y-4 text-[13px]">
                    <fieldset className="space-y-2">
                        <legend className="sr-only">Formule souhaitée</legend>
                        {autres.map((f) => (
                            <label key={f.id} className={`block rounded-lg border p-3 cursor-pointer ${choix === f.id ? 'border-brand-500 bg-brand-500/5' : 'border-border'}`}>
                                <span className="flex items-center gap-2 font-semibold text-content">
                                    <input type="radio" name="formule" checked={choix === f.id} onChange={() => setChoix(f.id)} />
                                    {f.nom} · {euros(f.prix_eur)} / mois
                                </span>
                                {f.description && <span className="block text-content-muted mt-1">{f.description}</span>}
                                <span className="block mt-2"><Contenu f={f} /></span>
                            </label>
                        ))}
                        {d.formule && (
                            <label className={`block rounded-lg border p-3 cursor-pointer ${choix === '' ? 'border-brand-500 bg-brand-500/5' : 'border-border'}`}>
                                <span className="flex items-center gap-2 font-semibold text-content">
                                    <input type="radio" name="formule" checked={choix === ''} onChange={() => setChoix('')} />
                                    Revenir aux modules à la carte
                                </span>
                            </label>
                        )}
                    </fieldset>
                    <p className="text-content-muted">Votre formule ne change pas toute seule : L’IAmani lit votre demande et vous répond. Le prix de la nouvelle formule s’applique à partir de son acceptation.</p>
                    <Field label="Un mot pour L’IAmani (facultatif)" counter={`${message.length}/500`}>
                        {(ids) => <Textarea {...ids} value={message} maxLength={500} rows={3} onChange={(e) => setMessage(e.target.value)} />}
                    </Field>
                    {erreur && <p role="alert" className="text-state-danger">{erreur}</p>}
                </div>
            </Modal>
        </section>
    );
};
